import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { Add, Close, FilterList } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";

// Dynamic filter bar for ScientificResearchTable. Each condition is
// `{ id, field, op, value }` and conditions combine with AND. The bar emits
// the final array up via `onChange`; the parent runs `applyConditions(rows)`
// from this module to filter rows before passing them to KPTable.
//
// Designed to be reusable across other work types — `optionsFromRows` lets
// the parent pre-compute autocomplete suggestions from the current dataset.

// Available field descriptors. Order = order in the "Thêm điều kiện" menu.
export const FILTER_FIELDS = [
  {
    key: "createdByName",
    label: "Người kê khai",
    type: "text",
    // Suggestions come from current rows.
    suggestionsFrom: (row) => row.createdByName,
  },
  {
    key: "approvedByName",
    label: "Người duyệt",
    type: "text",
    suggestionsFrom: (row) => row.approvedByName,
  },
  {
    key: "approvedAt",
    label: "Thời gian duyệt",
    type: "dateRange",
  },
  {
    key: "_status",
    label: "Trạng thái",
    type: "enum",
    options: [
      { value: "pending", label: "Chờ duyệt" },
      { value: "approved", label: "Đã duyệt" },
      { value: "revisionRequested", label: "Yêu cầu khai lại" },
      { value: "resubmitted", label: "Đã khai lại — chờ duyệt" },
    ],
  },
];

const OPERATORS = {
  text: [
    { value: "contains", label: "chứa" },
    { value: "equals", label: "bằng" },
    { value: "empty", label: "không có (—)" },
  ],
  dateRange: [{ value: "between", label: "trong khoảng" }],
  enum: [{ value: "in", label: "thuộc" }],
};

const stripAccents = (s = "") =>
  s.toString().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D").toLowerCase();

// Apply a list of conditions to a rows array with AND logic.
// Exported so the parent can run it without re-implementing.
export function applyConditions(rows, conditions) {
  if (!conditions?.length) return rows;
  return rows.filter((row) =>
    conditions.every((c) => {
      const field = FILTER_FIELDS.find((f) => f.key === c.field);
      if (!field) return true;
      const raw = row?.[c.field];
      switch (field.type) {
        case "text": {
          if (c.op === "empty") return !raw || String(raw).trim() === "";
          const needle = stripAccents((c.value || "").trim());
          if (!needle) return true;
          const hay = stripAccents(String(raw || ""));
          return c.op === "equals" ? hay === needle : hay.includes(needle);
        }
        case "dateRange": {
          if (!raw) return false;
          const t = new Date(raw).getTime();
          if (Number.isNaN(t)) return false;
          const fromTs = c.value?.from ? new Date(c.value.from).setHours(0, 0, 0, 0) : null;
          const toTs = c.value?.to ? new Date(c.value.to).setHours(23, 59, 59, 999) : null;
          if (fromTs !== null && t < fromTs) return false;
          if (toTs !== null && t > toTs) return false;
          return true;
        }
        case "enum": {
          const set = Array.isArray(c.value) ? c.value : [];
          if (!set.length) return true;
          return set.includes(raw);
        }
        default:
          return true;
      }
    }),
  );
}

// Generate a stable-ish id for a new condition row without Date.now (some
// callers may pre-populate from saved state).
let nextId = 1;
const newId = () => `c${nextId++}`;

function defaultConditionForField(field) {
  switch (field.type) {
    case "text":
      return { id: newId(), field: field.key, op: "contains", value: "" };
    case "dateRange":
      return { id: newId(), field: field.key, op: "between", value: { from: "", to: "" } };
    case "enum":
      return { id: newId(), field: field.key, op: "in", value: [] };
    default:
      return { id: newId(), field: field.key, op: "equals", value: "" };
  }
}

function ConditionRow({ condition, rows, onChange, onRemove }) {
  const field = FILTER_FIELDS.find((f) => f.key === condition.field);

  // Autocomplete suggestions for text fields — distinct values from rows.
  // Hook must run unconditionally; we guard inside instead of bailing early
  // before the call (rules-of-hooks).
  const suggestions = useMemo(() => {
    if (!field || field.type !== "text" || !field.suggestionsFrom) return [];
    const seen = new Set();
    const out = [];
    for (const r of rows || []) {
      const v = field.suggestionsFrom(r);
      if (v && !seen.has(v)) {
        seen.add(v);
        out.push(v);
      }
    }
    out.sort((a, b) => a.localeCompare(b, "vi"));
    return out;
  }, [field, rows]);

  if (!field) return null;
  const ops = OPERATORS[field.type] || [];

  return (
    <Grid container alignItems="center" spacing={1} style={{ marginBottom: 6 }}>
      <Grid item style={{ width: 150 }}>
        <Chip
          label={field.label}
          size="small"
          style={{ backgroundColor: "#e3f2fd", color: "#1565c0", fontWeight: 500 }}
        />
      </Grid>
      <Grid item style={{ width: 120 }}>
        <Select
          fullWidth
          variant="outlined"
          margin="dense"
          value={condition.op}
          onChange={(e) => onChange({ ...condition, op: e.target.value })}
          style={{ height: 38 }}
        >
          {ops.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs>
        {field.type === "text" && condition.op !== "empty" && (
          <Autocomplete
            freeSolo
            size="small"
            options={suggestions}
            value={condition.value || ""}
            onInputChange={(_, v) => onChange({ ...condition, value: v || "" })}
            renderInput={(p) => (
              <TextField
                {...p}
                variant="outlined"
                placeholder={`Nhập ${field.label.toLowerCase()}…`}
              />
            )}
          />
        )}
        {field.type === "text" && condition.op === "empty" && (
          <Typography variant="caption" style={{ color: "#888" }}>
            (không cần giá trị)
          </Typography>
        )}
        {field.type === "dateRange" && (
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                type="date"
                label="Từ ngày"
                InputLabelProps={{ shrink: true }}
                value={condition.value?.from || ""}
                onChange={(e) =>
                  onChange({ ...condition, value: { ...condition.value, from: e.target.value } })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                type="date"
                label="Đến ngày"
                InputLabelProps={{ shrink: true }}
                value={condition.value?.to || ""}
                onChange={(e) =>
                  onChange({ ...condition, value: { ...condition.value, to: e.target.value } })
                }
              />
            </Grid>
          </Grid>
        )}
        {field.type === "enum" && (
          <Select
            fullWidth
            multiple
            variant="outlined"
            margin="dense"
            value={Array.isArray(condition.value) ? condition.value : []}
            onChange={(e) => onChange({ ...condition, value: e.target.value })}
            style={{ height: 38 }}
            renderValue={(selected) =>
              field.options.filter((o) => selected.includes(o.value)).map((o) => o.label).join(", ")
            }
          >
            {field.options.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        )}
      </Grid>
      <Grid item>
        <IconButton size="small" onClick={onRemove} title="Xoá điều kiện">
          <Close fontSize="small" />
        </IconButton>
      </Grid>
    </Grid>
  );
}

export default function WorkflowFilterBar({ conditions, onChange, rows }) {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleAdd = (fieldKey) => {
    const field = FILTER_FIELDS.find((f) => f.key === fieldKey);
    if (!field) return;
    onChange([...(conditions || []), defaultConditionForField(field)]);
    setAnchorEl(null);
    setOpen(true);
  };

  const handleClear = () => onChange([]);

  const activeCount = (conditions || []).length;

  return (
    <Box mb={1}>
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <Button
          size="small"
          variant={open || activeCount > 0 ? "contained" : "outlined"}
          color={activeCount > 0 ? "primary" : "default"}
          startIcon={<FilterList />}
          onClick={() => setOpen((v) => !v)}
        >
          Bộ lọc nâng cao{activeCount > 0 ? ` (${activeCount})` : ""}
        </Button>
        {activeCount > 0 && (
          <Button size="small" onClick={handleClear} style={{ color: "#666" }}>
            Xoá tất cả
          </Button>
        )}
        {activeCount > 0 && !open && (
          <Box display="flex" style={{ gap: 4, overflow: "hidden", flexWrap: "wrap" }}>
            {conditions.map((c) => {
              const f = FILTER_FIELDS.find((x) => x.key === c.field);
              return (
                <Chip
                  key={c.id}
                  size="small"
                  label={f?.label || c.field}
                  onClick={() => setOpen(true)}
                  style={{ backgroundColor: "#e3f2fd", color: "#1565c0" }}
                />
              );
            })}
          </Box>
        )}
      </Box>

      <Collapse in={open}>
        <Paper variant="outlined" style={{ padding: 12, marginTop: 8 }}>
          {activeCount === 0 && (
            <Typography variant="caption" style={{ color: "#888", display: "block", marginBottom: 8 }}>
              Chưa có điều kiện lọc. Bấm <b>“+ Thêm điều kiện”</b> để bắt đầu — các điều kiện kết hợp theo AND.
            </Typography>
          )}
          {(conditions || []).map((c, i) => (
            <ConditionRow
              key={c.id}
              condition={c}
              rows={rows}
              onChange={(next) => {
                const copy = [...conditions];
                copy[i] = next;
                onChange(copy);
              }}
              onRemove={() => {
                const copy = [...conditions];
                copy.splice(i, 1);
                onChange(copy);
              }}
            />
          ))}
          <Box mt={1}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<Add />}
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              Thêm điều kiện
            </Button>
            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
              {FILTER_FIELDS.map((f) => (
                <MenuItem key={f.key} onClick={() => handleAdd(f.key)}>
                  {f.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
}
